import { useMemo, useState } from 'react';
import { computeFreeZones, snapStartInZone, type Busy } from '@/lib/agenda';
import { formatHeureMur, formatDurationMinutes } from '@/lib/dates';
import type { AvailableSlot } from '@/hooks/useSlots';

const HOUR_PX = 64; // hauteur d'une heure → 15 min = 16 px

type AgendaIntervention = {
  id: string;
  creneauDebut: string;
  creneauFin: string;
  nature: string;
};

type Props = {
  mecano: { id: string; prenom: string; nom: string; couleur: string };
  dateIso: string;
  durationMin: number;
  interventions: AgendaIntervention[];
  selectedSlot: AvailableSlot | null;
  onPickSlot: (slot: AvailableSlot) => void;
  isLoading: boolean;
  /** Plage d'ouverture (depuis AtelierConfig — défaut projet 9h/18h). */
  dayStartHour: number;
  dayEndHour: number;
};

function minutesToPx(min: number): number {
  return (min * HOUR_PX) / 60;
}

export default function MecanoDayAgenda({
  mecano,
  dateIso,
  durationMin,
  interventions,
  selectedSlot,
  onPickSlot,
  isLoading,
  dayStartHour,
  dayEndHour,
}: Props) {
  const [ghostStart, setGhostStart] = useState<number | null>(null);

  const { dayStart, dayEnd } = useMemo(() => {
    const [y, m, d] = dateIso.split('-').map(Number);
    return {
      dayStart: new Date(Date.UTC(y!, m! - 1, d!, dayStartHour, 0, 0, 0)),
      dayEnd: new Date(Date.UTC(y!, m! - 1, d!, dayEndHour, 0, 0, 0)),
    };
  }, [dateIso, dayStartHour, dayEndHour]);

  const busy: Busy[] = useMemo(
    () =>
      interventions.map((i) => ({
        debut: new Date(i.creneauDebut),
        fin: new Date(i.creneauFin),
      })),
    [interventions],
  );

  const freeZones = useMemo(
    () => computeFreeZones(busy, dayStart, dayEnd),
    [busy, dayStart, dayEnd],
  );

  const totalPx = minutesToPx((dayEndHour - dayStartHour) * 60);
  const topOf = (t: Date) => minutesToPx((t.getTime() - dayStart.getTime()) / 60_000);
  const heightOf = (a: Date, b: Date) => minutesToPx((b.getTime() - a.getTime()) / 60_000);

  const hours = [];
  for (let h = dayStartHour; h <= dayEndHour; h++) hours.push(h);

  function pickAt(zone: { debut: Date; fin: Date }, clientY: number, rect: DOMRect) {
    const offsetY = clientY - rect.top;
    const zoneMin = (zone.fin.getTime() - zone.debut.getTime()) / 60_000;
    const clickMin = Math.max(0, Math.min(zoneMin, (offsetY / rect.height) * zoneMin));
    const clickTime = new Date(zone.debut.getTime() + clickMin * 60_000);
    return snapStartInZone(clickTime, zone, durationMin);
  }

  // Créneau sélectionné visible si même mécano et tombant dans ce jour
  const selectedStart =
    selectedSlot && selectedSlot.mecanicienId === mecano.id
      ? new Date(selectedSlot.debut)
      : null;
  const selectedVisible =
    selectedStart &&
    selectedStart.getTime() >= dayStart.getTime() &&
    selectedStart.getTime() < dayEnd.getTime();

  if (isLoading) {
    return (
      <div className="border border-border-light rounded-md p-4 text-sm text-text-muted">
        Chargement de l'agenda…
      </div>
    );
  }

  return (
    <div className="border border-border-light rounded-md overflow-hidden">
      <div className="px-3 py-2 border-b border-border-light bg-surface-bg flex items-center gap-2">
        <span
          className="w-5 h-5 rounded-sm flex items-center justify-center text-text-on-orange font-bold text-xs"
          style={{ backgroundColor: mecano.couleur }}
          aria-hidden
        >
          {mecano.prenom.charAt(0).toUpperCase()}
        </span>
        <span className="text-sm font-semibold text-text-primary">
          {mecano.prenom} {mecano.nom}
        </span>
        <span className="text-xs text-text-muted ml-auto">Clique dans un trou libre</span>
      </div>

      <div className="relative max-h-[420px] overflow-y-auto">
        <div
          className="relative"
          style={{ height: totalPx }}
          onMouseLeave={() => setGhostStart(null)}
        >
          {/* Graduations horaires */}
          {hours.map((h) => {
            const t = new Date(dayStart.getTime() + (h - dayStartHour) * 60 * 60_000);
            return (
              <div
                key={h}
                className="absolute left-0 right-0 border-t border-border-light"
                style={{ top: topOf(t) }}
              >
                <span className="absolute -top-2 left-1 text-[10px] font-mono text-text-muted bg-surface-white px-0.5">
                  {String(h).padStart(2, '0')}:00
                </span>
              </div>
            );
          })}

          {/* Colonne timeline (décalée pour laisser la place aux libellés d'heure) */}
          <div className="absolute left-12 right-2 top-0 bottom-0">
            {/* Zones libres cliquables */}
            {freeZones.map((zone, idx) => {
              const top = topOf(zone.debut);
              const height = heightOf(zone.debut, zone.fin);
              const tooShort =
                snapStartInZone(zone.debut, zone, durationMin) === null;
              if (tooShort) {
                return (
                  <div
                    key={`free-${idx}`}
                    className="absolute left-0 right-0 rounded-sm bg-surface-bg/60 border border-dashed border-border-light flex items-center justify-center"
                    style={{ top, height }}
                  >
                    {height >= 18 && (
                      <span className="text-[10px] text-text-muted uppercase tracking-wider">
                        trop court
                      </span>
                    )}
                  </div>
                );
              }
              return (
                <div
                  key={`free-${idx}`}
                  role="button"
                  tabIndex={0}
                  className="absolute left-0 right-0 rounded-sm bg-status-termine-bg/40 hover:bg-status-termine-bg/70 cursor-pointer transition-colors"
                  style={{ top, height }}
                  onMouseMove={(e) => {
                    const start = pickAt(
                      zone,
                      e.clientY,
                      e.currentTarget.getBoundingClientRect(),
                    );
                    setGhostStart(start ? start.getTime() : null);
                  }}
                  onClick={(e) => {
                    const start = pickAt(
                      zone,
                      e.clientY,
                      e.currentTarget.getBoundingClientRect(),
                    );
                    if (start) {
                      onPickSlot({
                        mecanicienId: mecano.id,
                        debut: start.toISOString(),
                        fin: new Date(start.getTime() + durationMin * 60_000).toISOString(),
                      });
                    }
                  }}
                />
              );
            })}

            {/* Blocs occupés — bornés à la plage 9h-18h (le libellé garde les vraies heures) */}
            {interventions.map((i) => {
              const debut = new Date(i.creneauDebut);
              const fin = new Date(i.creneauFin);
              // Clampe l'affichage à la journée visible ; ignore si entièrement hors plage.
              const visDebut = new Date(Math.max(debut.getTime(), dayStart.getTime()));
              const visFin = new Date(Math.min(fin.getTime(), dayEnd.getTime()));
              if (visFin.getTime() <= visDebut.getTime()) return null;
              return (
                <div
                  key={i.id}
                  className="absolute left-0 right-0 rounded-sm px-2 py-0.5 overflow-hidden pointer-events-none"
                  style={{
                    top: topOf(visDebut),
                    height: heightOf(visDebut, visFin),
                    backgroundColor: `${mecano.couleur}22`,
                    borderLeft: `3px solid ${mecano.couleur}`,
                  }}
                >
                  <div className="text-xs font-medium text-text-primary truncate">
                    {i.nature}
                  </div>
                  <div className="text-[10px] font-mono text-text-muted">
                    {formatHeureMur(debut)} → {formatHeureMur(fin)}
                  </div>
                </div>
              );
            })}

            {/* Fantôme au survol */}
            {ghostStart !== null && (!selectedVisible || ghostStart !== selectedStart!.getTime()) && (
              <div
                className="absolute left-0 right-0 rounded-sm border-2 border-dashed border-ktm-orange bg-ktm-orange-light/40 pointer-events-none"
                style={{
                  top: topOf(new Date(ghostStart)),
                  height: minutesToPx(durationMin),
                }}
              />
            )}

            {/* Créneau sélectionné */}
            {selectedVisible && (
              <div
                className="absolute left-0 right-0 rounded-sm bg-ktm-orange-light border-2 border-ktm-orange flex flex-col justify-center px-2 pointer-events-none"
                style={{
                  top: topOf(selectedStart!),
                  height: minutesToPx(durationMin),
                }}
              >
                <span className="text-xs font-semibold text-ktm-orange">
                  Choisi · {formatDurationMinutes(durationMin)}
                </span>
                <span className="text-[10px] font-mono text-ktm-orange">
                  {formatHeureMur(selectedStart!)} →{' '}
                  {formatHeureMur(new Date(selectedStart!.getTime() + durationMin * 60_000))}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
